import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useCreateIntegrationMutation } from "@modules/erpIntegrations/services/mutations";
import { listIntegrationsQueryKey } from "@modules/erpIntegrations/services/queries";
import { applyIntegrationApiErrors } from "@modules/erpIntegrations/utils";
import {
  IntegrationFormValues,
  integrationSchema,
} from "@modules/erpIntegrations/validations";

export function useCreateIntegrationModal(
  modalRef: RefObject<ModalRef>,
  onComplete?: () => void
) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<IntegrationFormValues>({
    resolver: zodResolver(integrationSchema()),
    mode: "onBlur",
    defaultValues: {
      name: "",
      description: "",
      documentation_url: "",
      instructions: "",
      auth_endpoint: null,
      auth_token_response_key: "",
      active: true,
      features: [],
      credential_schema: [],
    },
  });

  const { mutateAsync, isPending } = useCreateIntegrationMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listIntegrationsQueryKey,
        refetchType: "all",
      });

      open?.({
        type: "success",
        message: translate("v1/integration_admin.success.create"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
    onError: (error) => {
      applyIntegrationApiErrors(error, methods.setError);
    },
  });

  return {
    methods,
    mutateAsync,
    isPending,
  };
}
